import * as React from "react"
import { Check, CircleCheck, Loader2, TriangleAlert } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

/*
  UnsavedBar — sticky bar at the bottom of an editable page/section:
  "● 3 unsaved changes · detail ……… Discard  [✓ Save changes]".

    <UnsavedBar
      count={dirtyCount}                 // 0 hides the bar (unless just saved)
      detail="Theme: Sara Light → White / Navy / Green"
      status={status}                    // 'idle' | 'saving' | 'saved' | 'error'
      error="Network error — nothing was saved."
      onSave={save}
      onDiscard={discard}
      saveLabel="Save changes"
    />

  After a save, pass count={0} and status="saved": the bar shows "All changes saved" for
  `savedDuration` ms (default 2500) and then hides itself. Pair with useUnsavedChanges(count > 0).
  tone="brand": the navy bar of the kit (white text, lime Save) — e.g. beside a navy BulkBar; default "surface".

  While the sticky bar is visible near the bottom of the viewport it sets `--toast-bottom-offset` on
  <html> (measured from where the bar really is, kept current on scroll / resize); the app <Toaster/>
  adds it to its bottom offset, so toasts sit above Discard / Save / Try again.
*/

// Visible sticky bars → how far the toasts must rise to clear them.
const toastClearances = new Map()
let clearanceSequence = 0
// Toasts live in the bottom band of the viewport; a bar outside this band never collides with them.
const TOAST_BAND_PX = 140
// Space between a lifted toast and the bar: 24px toaster offset − 4 → 20px on desktop, 12px on mobile.
const CLEARANCE_TRIM_PX = 4

function applyToastClearance() {
  if (typeof document === "undefined") return
  const root = document.documentElement
  const needed = Math.max(0, ...toastClearances.values())
  if (needed > 0) root.style.setProperty("--toast-bottom-offset", `${Math.ceil(needed)}px`)
  else root.style.removeProperty("--toast-bottom-offset")
}

function useToastClearance(ref, active) {
  React.useLayoutEffect(() => {
    const node = ref.current
    if (!active || !node || typeof window === "undefined") return undefined
    clearanceSequence += 1
    const key = clearanceSequence
    let cancelScheduled = null
    const measure = () => {
      cancelScheduled = null
      const rect = node.getBoundingClientRect()
      const viewport = window.innerHeight || document.documentElement.clientHeight
      // Measured, not assumed: page padding changes where a sticky bar actually sits.
      const inToastBand = rect.height > 0 && rect.top < viewport && rect.bottom > viewport - TOAST_BAND_PX
      toastClearances.set(key, inToastBand ? Math.max(0, viewport - rect.top - CLEARANCE_TRIM_PX) : 0)
      applyToastClearance()
    }
    const schedule = () => {
      if (cancelScheduled) return
      if (window.requestAnimationFrame) {
        const id = window.requestAnimationFrame(measure)
        cancelScheduled = () => window.cancelAnimationFrame(id)
      } else {
        const id = setTimeout(measure, 16)
        cancelScheduled = () => clearTimeout(id)
      }
    }
    measure()
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null
    observer?.observe(node)
    // capture: also hears scrolling inside the app's scroll containers
    document.addEventListener("scroll", schedule, { capture: true, passive: true })
    window.addEventListener("resize", schedule)
    return () => {
      observer?.disconnect()
      document.removeEventListener("scroll", schedule, { capture: true })
      window.removeEventListener("resize", schedule)
      cancelScheduled?.()
      toastClearances.delete(key)
      applyToastClearance()
    }
  }, [active, ref])
}

function UnsavedBar({
  count = 0,
  detail,
  status = "idle",
  saving,
  error,
  onSave,
  onDiscard,
  saveLabel = "Save changes",
  discardLabel = "Discard",
  savedLabel = "All changes saved",
  savedDuration = 2500,
  sticky = true,
  tone = "surface",
  className,
  children,
  ...props
}) {
  const brand = tone === "brand"
  const isSaving = saving || status === "saving"
  const isSaved = !isSaving && status === "saved" && count === 0
  const [showSaved, setShowSaved] = React.useState(isSaved)
  const barRef = React.useRef(null)
  const visible = count > 0 || (isSaved && showSaved)
  useToastClearance(barRef, visible && sticky)

  React.useEffect(() => {
    if (!isSaved) {
      setShowSaved(false)
      return undefined
    }
    setShowSaved(true)
    const timer = setTimeout(() => setShowSaved(false), savedDuration)
    return () => clearTimeout(timer)
  }, [isSaved, savedDuration])

  if (!visible) return null

  const noun = count === 1 ? "unsaved change" : "unsaved changes"

  return (
    <div
      ref={barRef}
      role="region"
      aria-label="Unsaved changes"
      data-testid="unsaved-bar"
      data-status={isSaving ? "saving" : isSaved ? "saved" : status}
      className={cn(
        "z-20 mt-4 flex min-h-14 flex-wrap items-center gap-x-3 gap-y-2 rounded-card border px-4 py-2.5 shadow-overlay",
        brand ? "border-transparent bg-[color:var(--navy)] text-white" : "border-line bg-surface text-ink",
        // --app-bottom-nav-height: the phone tab bar (0 elsewhere) — the bar floats above it.
        sticky && "sticky bottom-[calc(1rem+var(--app-bottom-nav-height,0px))]",
        className
      )}
      {...props}
    >
      {isSaved ? (
        <div className="flex items-center gap-2 text-[13.5px] font-semibold" aria-live="polite">
          <CircleCheck className={cn("h-4 w-4", brand ? "text-lime" : "text-status-green")} aria-hidden="true" />
          {savedLabel}
        </div>
      ) : (
        <>
          <div className="flex min-w-0 flex-1 items-center gap-2" aria-live="polite">
            <span className="ui-tone-dot tone-amber h-2 w-2" aria-hidden="true" />
            <span className="whitespace-nowrap text-[13.5px] font-bold">
              {count} {noun}
            </span>
            {detail ? <span className={cn("min-w-0 truncate text-[13px]", brand ? "text-white/65" : "text-ink-3")}>{detail}</span> : null}
            {status === "error" ? (
              <span className={cn("flex min-w-0 items-center gap-1.5 text-[12.5px] font-semibold", brand ? "text-[#fca5a5]" : "text-status-red-ink")} role="alert">
                <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">{error || "Could not save. Try again."}</span>
              </span>
            ) : null}
          </div>
          {children}
          <div className="ml-auto flex items-center gap-2">
            {onDiscard ? (
              <Button type="button" variant="ghost" onClick={onDiscard} disabled={isSaving} className={brand ? "text-white hover:bg-white/10 hover:text-white" : undefined} data-testid="unsaved-discard">
                {discardLabel}
              </Button>
            ) : null}
            {onSave ? (
              <Button type="button" variant="primary" onClick={onSave} disabled={isSaving || count <= 0} className={brand ? "bg-lime text-[color:var(--navy-950)] hover:bg-lime-400" : undefined} data-testid="unsaved-save">
                {isSaving ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Check aria-hidden="true" />}
                {isSaving ? "Saving…" : status === "error" ? "Try again" : saveLabel}
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}

export { UnsavedBar, useToastClearance }
